import { useNavigate } from 'react-router-dom';
import { CHAT_CONVERSATION_PATH } from '../../pathNames';
import { ChatState, FakeImage, SharedImage, ChatMessage } from '../../types';
import { useChatContext } from '../../context/ChatContextProvider';
import { v4 as uuidv4 } from 'uuid';
import { DEFAULT_BOT_NAME, DEFAULT_SCENARIO } from '../../chatConstants';
import { toast } from 'react-toastify';
import useModalPrompt from '../../components/Modals/PromptModal/useModalPrompt';
import {
  TEXT_PROMPT_BOT_NAME,
  BOT_NAME_OPTIONS,
  TEXT_PROMPT_CHAT_SCENARIO,
  TEXT_PROMPT_YOUR_NAME,
  TEXT_PROMPT_CHAT_SCENARIO_OPTIONS,
  USER_NAME_OPTIONS,
} from './chatHelpers';
import useChatModeration from './useChatModeration';
const useStartChat = () => {
  const {
    addNewChat,
    setActiveChat,
    userName,
    setUserName,
    activeChatId,
    chats,
    setShowBiggerImage,
  } = useChatContext();
  const navigate = useNavigate();
  const promptModal = useModalPrompt();
  const validateLatestMessageContent = useChatModeration();

  const startChatWithSharedImage = async (sharedImage: SharedImage) => {
    if (!sharedImage) return;

    setShowBiggerImage(true);

    const botName = await promptModal(TEXT_PROMPT_BOT_NAME, BOT_NAME_OPTIONS);
    const chatScenario = await promptModal(
      TEXT_PROMPT_CHAT_SCENARIO,
      TEXT_PROMPT_CHAT_SCENARIO_OPTIONS,
    );

    if (!userName) {
      const newName = await promptModal(
        TEXT_PROMPT_YOUR_NAME,
        USER_NAME_OPTIONS,
      );
      if (newName) setUserName(newName);
    }

    const fakeImageObjects = sharedImage.album ?? [sharedImage.image];
    const allBotImages = fakeImageObjects.map((image) => image.imageUrl);

    const newChat: ChatState = {
      id: uuidv4(),
      messages: [],
      botName: botName || DEFAULT_BOT_NAME,
      chatScenario: chatScenario || DEFAULT_SCENARIO,
      activeImage: sharedImage.image.imageUrl,
      fakeImageObjects,
      allBotImages,
      isHomePageStartedChat: false,
    };

    addNewChat(newChat);
    setActiveChat(newChat.id);
    navigate(CHAT_CONVERSATION_PATH);
  };

  const startChatWithFakeImage = async (fakeImage: FakeImage) => {
    if (!fakeImage) return;
    setShowBiggerImage(true);
    if (fakeImage.imageUrl === '/placeholder.png') {
      toast.error(
        'Please wait for the image to generate to start a chat or use another image',
      );
      return;
    }

    const botName = await promptModal(TEXT_PROMPT_BOT_NAME, BOT_NAME_OPTIONS);
    const chatScenario = await promptModal(
      TEXT_PROMPT_CHAT_SCENARIO,
      TEXT_PROMPT_CHAT_SCENARIO_OPTIONS,
    );

    if (!userName) {
      const newName = await promptModal(
        TEXT_PROMPT_YOUR_NAME,
        USER_NAME_OPTIONS,
      );
      if (newName) setUserName(newName);
    }

    const newChat: ChatState = {
      id: uuidv4(),
      messages: [],
      botName: botName || DEFAULT_BOT_NAME,
      chatScenario: chatScenario || DEFAULT_SCENARIO,
      activeImage: fakeImage.imageUrl,
      fakeImageObjects: [fakeImage],
      allBotImages: [fakeImage.imageUrl],
      isHomePageStartedChat: false,
    };

    addNewChat(newChat);
    setActiveChat(newChat.id);
    navigate(CHAT_CONVERSATION_PATH);
  };

  const startChatWithoutImageInHomePage = async (
    firstMessage: string,
    imageUrlToUse?: string,
  ) => {
    const chatImagesToUse = [
      'https://onlyfakes-images-sdapi.s3.eu-north-1.amazonaws.com/generations/4d89a860-b216-4b63-bc6f-626245cf43cd.jpeg',
      'https://onlyfakes-images-sdapi.s3.eu-north-1.amazonaws.com/generations/f66cf279-a310-4209-ab67-d78ce45544c2.jpeg',
      'https://onlyfakes-images-sdapi.s3.eu-north-1.amazonaws.com/generations/ec33de88-8e1c-479a-84c3-fade489047d1.jpeg',
    ];
    const randomImage =
      chatImagesToUse[Math.floor(Math.random() * chatImagesToUse.length)];
    const imageToUse = imageUrlToUse || randomImage;

    const botName = 'Uncensored Bot';
    const chatScenario = `${firstMessage}`;

    const initialMessage: ChatMessage = {
      role: 'user',
      content: firstMessage,
      timestamp: new Date().toISOString(),
    };

    const { hasProhibitedContent } = validateLatestMessageContent([
      initialMessage,
    ]);

    if (hasProhibitedContent) return;

    const newChat: ChatState = {
      id: uuidv4(),
      messages: [initialMessage],
      botName: botName,
      chatScenario: chatScenario,
      activeImage: imageToUse,
      fakeImageObjects: [],
      allBotImages: [imageToUse],
      isHomePageStartedChat: true,
    };

    setShowBiggerImage(false);

    addNewChat(newChat);
    setActiveChat(newChat.id);
    navigate(CHAT_CONVERSATION_PATH);
  };

  const startNewChatWithOngoingChat = () => {
    const currentChat = chats.find((chat) => chat.id === activeChatId);
    if (!currentChat) return;

    const newChat: ChatState = {
      id: uuidv4(),
      botName: currentChat.botName,
      chatScenario: currentChat.chatScenario,
      messages: [],
      activeImage: currentChat.activeImage,
      fakeImageObjects: currentChat.fakeImageObjects,
      allBotImages: currentChat.allBotImages,
      isHomePageStartedChat: false,
    };

    addNewChat(newChat);
    setActiveChat(newChat.id);
    navigate(CHAT_CONVERSATION_PATH);
  };

  return {
    startChatWithSharedImage,
    startChatWithFakeImage,
    startNewChatWithOngoingChat,
    startChatWithoutImageInHomePage,
  };
};

export default useStartChat;
